import { useQuery } from "@tanstack/react-query";
import { Card } from "../../../components/Card";
import { api } from "../../../services/api";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { ReactComponent as CheckIcon } from "../../../assets/icons/check.svg";


const DOW_LABELS = ["Su", "M", "T", "W", "Th", "F", "S"] as const;

type WeekPracticeDay = { date: string; practiced: boolean };

type WeekPracticePayload = {
  weekStartDate: string;
  days: WeekPracticeDay[];
  updatedAt?: string;
};

function ymdFromIso(iso: string): string {
  return iso.slice(0, 10);
}

function utcTodayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function dowLabelUtc(ymd: string): string {
  const dow = new Date(`${ymd}T12:00:00.000Z`).getUTCDay();
  return DOW_LABELS[dow];
}

export function CallStreak() {
  const { data: weekPractice } = useQuery({
    queryKey: ["weekPractice"],
    queryFn: () => api.getWeekPractice(),
  });
  const { data: streak } = useQuery({
    queryKey: ["streak"],
    queryFn: () => api.getStreak(),
  });

  const weekPracticeBody = weekPractice?.data as WeekPracticePayload | undefined;
  const days = weekPracticeBody?.days ?? [];
  const streakBody = streak?.data as { longestStreakLength?: number, currentStreakLength?: number } | undefined;

  const todayYmd = utcTodayYmd();
  const streakDays = streakBody?.currentStreakLength ?? 0;
  const streakLabel =
    streakDays === 1 ? "1 day streak" : `${streakDays} days streak`;

  return (
    <Card className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <span className="text-black text-[clamp(13px,1.5vw,16px)] font-semibold leading-[17px]">Call Streak</span>
        <span className="text-black text-[10px] leading-[13px]">
          Personal Best: {streakBody?.longestStreakLength ?? "0"} {(streakBody?.longestStreakLength ?? 0) === 1 ? "day" : "days"}
        </span>
      </div>
      <div className="text-gray-601 text-sm font-semibold leading-[18.2px]">{streakLabel}</div>
      <div className="flex gap-2 justify-between">
        {days.map((day) => {
          const ymd = ymdFromIso(day.date);
          const isPast = ymd < todayYmd;
          const isToday = ymd === todayYmd;
          const filled = day.practiced;
          return (
            <div key={day.date} className="flex flex-col items-center gap-1">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center ${isPast ? filled ? "bg-primary5" : "bg-gray-402" : isToday ? `bg-primary5 ${!filled ? "opacity-50" : ""}` : "bg-gray-402"}`}
              >
                {isPast && <>
                    {filled ? <CheckIcon className="w-4 h-4 relative [&>path]:stroke-white [&>path]:stroke-[1.75px]" /> : <CloseIcon className="w-4 h-4 relative [&>path]:stroke-white [&>path]:stroke-[1.75px]" />}
                </>}
                {
                    isToday && filled ? <>
                    <CheckIcon className="w-4 h-4 relative [&>path]:stroke-white [&>path]:stroke-[1.75px]" />
                    </> : null
                }
                {
                    !isPast && !isToday && <CheckIcon className="w-4 h-4 relative [&>path]:stroke-white [&>path]:stroke-[1.75px]" />
                }
              </div>
              <div className="text-[10px] text-gray-601 leading-none">{dowLabelUtc(ymd)}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
